/**
 * The settings document, and the one write that touches it (`100-settings.md`).
 *
 * **A settings write is a write like any other**: it goes through `outbox.enqueue()`, never a
 * raw `fetch` (`112-offline-drafts.md` §5). `settings.patch` is COALESCABLE and every entry
 * carries the same `entityId`, so dragging the max-tokens spinner does not queue forty
 * requests — the outbox merges them into one, keeping the last value of each key.
 *
 * That merge is also why the PATCH body must be the *deep* patch and not the whole document:
 * coalescing does a shallow `{...target.body, ...input.body}` merge, so two patches to
 * different groups (`{ui:{theme}}` then `{calendar:{timezone}}`) merge cleanly, while two
 * patches to the SAME group would have the later one replace the earlier group object
 * wholesale. `enqueuePatch` below therefore merges the group itself before enqueueing, against
 * the cache we have just optimistically written — so `{ai:{thinking}}` after `{ai:{model}}`
 * carries both.
 */
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { keys } from '@/api/keys';
import {
  getSettings,
  resolveSettings,
  type Settings,
  type SettingsDto,
  type SettingsPatch,
} from '@/api/endpoints/settings';
import { outbox } from '@/offline/outbox';

/** One document, one cache key, one invalidation (`100-settings.md` §3). */
const ENTITY_ID = 'settings';

export function useSettingsQuery(): UseQueryResult<SettingsDto> {
  return useQuery({
    queryKey: keys.settings(),
    queryFn: getSettings,
    // `111` §3.2: we are the only writer, and every settings write patches this cache directly.
    staleTime: Infinity,
    gcTime: Infinity,
    // A settings document that will not load must not retry forever behind a screen that has
    // already fallen back to the defaults.
    retry: 1,
  });
}

/** Always a complete document — the defaults fill the gaps while the query is in flight. */
export function useSettings(): Settings {
  const query = useSettingsQuery();
  return resolveSettings(query.data);
}

function merge(current: SettingsDto | undefined, patch: SettingsPatch): SettingsDto {
  return {
    ...current,
    ...(patch.ai === undefined ? {} : { ai: { ...current?.ai, ...patch.ai } }),
    ...(patch.ui === undefined ? {} : { ui: { ...current?.ui, ...patch.ui } }),
    ...(patch.calendar === undefined
      ? {}
      : { calendar: { ...current?.calendar, ...patch.calendar } }),
  };
}

/**
 * Optimistic, with rollback (`100-settings.md` §5: "a failed write reverts the control and
 * shows an inline error").
 *
 * As everywhere else in this app, `onError` does NOT fire merely because the network is down —
 * `mutationFn` resolves as soon as the entry is durable in IndexedDB. It fires only for a
 * permanent failure the outbox will not retry (a 422 from the registry), which is the one case
 * where reverting the control is honest.
 */
export function useUpdateSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patch: SettingsPatch) => {
      // The body is built from the freshly-patched cache, so a coalesced merge cannot lose a
      // sibling key inside the same group. See the header.
      const current = queryClient.getQueryData<SettingsDto>(keys.settings());
      const body: SettingsPatch = {
        ...(patch.ai === undefined ? {} : { ai: { ...current?.ai, ...patch.ai } }),
        ...(patch.ui === undefined ? {} : { ui: { ...current?.ui, ...patch.ui } }),
        ...(patch.calendar === undefined
          ? {}
          : { calendar: { ...current?.calendar, ...patch.calendar } }),
      };

      return outbox.enqueue({
        kind: 'settings.patch',
        entityId: ENTITY_ID,
        label: 'settings',
        request: { method: 'PATCH', path: '/api/settings', body },
      });
    },

    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: keys.settings() });
      const previous = queryClient.getQueryData<SettingsDto>(keys.settings());

      queryClient.setQueryData<SettingsDto>(keys.settings(), (old) => merge(old, patch));

      return { previous };
    },

    onError: (_error, _patch, context) => {
      if (context !== undefined) queryClient.setQueryData(keys.settings(), context.previous);
    },
  });
}

/** A stable `(patch) => void`. The screen calls this from a dozen controls. */
export function usePatchSettings(): (patch: SettingsPatch) => void {
  const { mutate } = useUpdateSettings();
  return useCallback(
    (patch: SettingsPatch) => {
      mutate(patch);
    },
    [mutate],
  );
}
